import React from 'react';
import {
  ROUTES,
  INTERNAL_ROUTING,
  EVENTS,
  FAVORITES_ENTITIES,
  CATEGORY_TYPES,
} from 'shared/constants';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import analyticsService from '../../../helpers/analytics.service';
import { notification } from 'antd';
import { Lang } from 'shared/constants';
import { ConfirmationPopupComponent } from '../../../components/common/confirmation-popup';
import { UserList } from 'src/components/user-list';
import { ENTITY, useColumns } from 'src/components/user-list/use-columns.hooks';
import styles from './contacts.module.scss';
import { ShareBoxComponent } from 'src/components/share';
import { FavoritesButton } from 'src/components/favorites-button';
import { ContactDetailsComponent } from 'src/components/contact-details';
import { useSelector } from 'react-redux';
import { isRoot } from 'shared/store/selectors/auth.selector';
import { Button } from 'src/components/design-system';

export const ITEM_SIZE = 74;

const renderActions = contact => (
  <div className={styles.actionIcons}>
    <ShareBoxComponent
      path={ROUTES.CONTACTS_PREVIEW.replace(':id', contact.id)}
      id={contact.id}
      title={contact?.firstName}
      entityType={CATEGORY_TYPES.KNOWLEDGE}
      iconClassName={styles.actionIcon}
      className={styles.buttonWrapper}
    />

    <FavoritesButton
      id={contact.id}
      entity={contact.isUser ? FAVORITES_ENTITIES.USER : FAVORITES_ENTITIES.CONTACT}
      background={styles.transparentBackground}
      wrapper={styles.buttonWrapper}
      className={styles.actionIcon}
    />
  </div>
);

const BulkUploadButton = ({ lang }) => (
  <Button className={styles.bulkUploadButton} href={ROUTES.SETTINGS_CONTACT_UPLOAD} type="default">
    {lang.BULK_UPLOAD}
  </Button>
);

const ContactsComponent = React.memo(function ContactsComponent({
  lang = Lang.CONTACT_SCREEN,
  contacts,
  deleteContact,
}) {
  const [details, setDetails] = React.useState({});
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [api, contextHolder] = notification.useNotification();
  const [deleteContactEntity, setDeleteContactEntity] = React.useState(null);
  const navigate = useNavigate();
  const { search } = useLocation();
  const isRootUser = useSelector(isRoot);

  const columns = useColumns({ entity: ENTITY.CONTACT, renderActions });

  React.useEffect(() => {
    const urlParams = new URLSearchParams(search);
    const id = urlParams.get(INTERNAL_ROUTING.QUERY_PARAMS.CONTACTS);
    if (id) {
      const contact = contacts.find(contact => contact.id === id);
      setDetails(contact);
      setDetailsOpen(true);
    } else {
      setDetails({});
    }
  }, [search, contacts]);

  const openNotification = useCallback(
    ({ message, type = 'success' }) => {
      api[type]({
        message,
        placement: 'bottomRight',
        duration: 10,
      });
    },
    [api],
  );

  const addContact = useCallback(() => {
    analyticsService.track(EVENTS.CONTACT_ADD.ADD_CLICK);
    setDetails({});
    setDetailsOpen(true);
  }, []);

  const closeDetailsSidebar = useCallback(() => {
    navigate(ROUTES.CONTACTS);
    setDetailsOpen(false);
  }, [navigate]);

  const openPreview = useCallback(
    id => {
      navigate(ROUTES.CONTACTS_PREVIEW.replace(':id', id));
    },
    [navigate],
  );

  const onDeleteContact = useCallback(contact => setDeleteContactEntity(contact), []);
  const doDeleteContact = useCallback(async () => {
    const res = await deleteContact({ id: deleteContactEntity.id });

    if (!res?.error) {
      openNotification({ message: lang.NOTIFICATION_DELETE_SUCCESS });
      setDeleteContactEntity(null);
      closeDetailsSidebar();
    }
  }, [deleteContactEntity, deleteContact, openNotification, lang, closeDetailsSidebar]);

  const cancelDeleteContact = useCallback(() => setDeleteContactEntity(null), []);

  const contactDetails = useMemo(
    () => (
      <ContactDetailsComponent
        contact={details}
        close={closeDetailsSidebar}
        open={detailsOpen}
        openNotification={openNotification}
        onDeleteContact={onDeleteContact}
      />
    ),
    [details, closeDetailsSidebar, detailsOpen, openNotification, onDeleteContact],
  );

  const bulkUploadButton = useMemo(
    () => (isRootUser ? <BulkUploadButton lang={lang} /> : null),
    [isRootUser, lang],
  );

  return (
    <>
      <UserList
        users={contacts}
        columns={columns}
        pageTitle={lang.TITLE}
        buttonText={lang.ADD_NEW_CONTACT}
        onAdd={addContact}
        onDelete={onDeleteContact}
        openNotification={openNotification}
        notificationContextHolder={contextHolder}
        details={contactDetails}
        openPreview={openPreview}
        bulkUploadButton={bulkUploadButton}
      />
      <ConfirmationPopupComponent
        open={!!deleteContactEntity}
        title={lang.DELETE_CONTACT_POPUP_TITLE}
        message={lang.DELETE_CONTACT_POPUP_TEXT.replace(
          '{name}',
          `${deleteContactEntity?.firstName ?? ''} ${deleteContactEntity?.lastName || ''}`,
        )}
        confirmMessage={lang.DELETE_CONTACT_POPUP_CONFIRM}
        dismissMessage={lang.DELETE_CONTACT_POPUP_CANCEL}
        onConfirm={doDeleteContact}
        onDismiss={cancelDeleteContact}
      />
    </>
  );
});

export { ContactsComponent };
